import { useMemo } from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import { SyncEvent, MarketingEmailSendStatusEvent } from '../api/generated/model';

/**
 * Hook to get the sync state for a specific sync operation by sync_id.
 *
 * Prefer this over useSyncState() when a UI element tracks ONE sync it
 * initiated (e.g. a SyncButton): every sync has a unique sync_id, so the
 * hook won't be triggered by unrelated syncs on the same integration
 * (periodic, webhook, other tab, a different button, etc.).
 *
 * @param syncId - sync_id from the POST /.../sync response; undefined is a no-op
 */
export function useSyncStateBySyncId(syncId: string | undefined): SyncEvent | undefined {
  const { activeSyncs } = useNotifications();
  return useMemo(() => {
    if (!syncId) return undefined;
    return activeSyncs.get(syncId);
  }, [activeSyncs, syncId]);
}

/**
 * Same shape/intent as useSyncStateBySyncId, but for marketing email sends.
 * Returns the latest status event for a specific send_id, or undefined.
 */
export function useMarketingEmailSendStateBySendId(sendId: string | undefined): MarketingEmailSendStatusEvent | undefined {
  const { activeMarketingEmailSends } = useNotifications();
  return useMemo(() => {
    if (!sendId) return undefined;
    return activeMarketingEmailSends.get(sendId);
  }, [activeMarketingEmailSends, sendId]);
}

/**
 * Hook to get the most recent sync event for a given integration.
 *
 * Use this for page-level status displays that want to know "is there any
 * sync happening on this integration right now" (including syncs the page
 * didn't initiate — periodic, webhook-triggered, other tab). For buttons
 * that should only reflect their OWN sync, use useSyncStateBySyncId instead.
 *
 * @param integrationId - Integration ID to filter by (e.g., 'events', quickbooks integration ID, etc.)
 * @returns The most recent sync event for the integration, or undefined
 */
export function useSyncState(integrationId: string): SyncEvent | undefined {
  const { activeSyncs } = useNotifications();

  // Use useMemo to only recalculate when relevant syncs change
  const result = useMemo(() => {
    // Get all syncs for this integration ID
    const filteredSyncs = Array.from(activeSyncs.values())
      .filter(sync => sync.integration_id === integrationId);

    // Return the most recent sync event
    return filteredSyncs
      .sort((a, b) => {
        const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return timeB - timeA;
      })[0];
  }, [activeSyncs, integrationId]);

  // Debug logging
  if (result && (result.status === 'started' || result.status === 'progress')) {
    console.log(`[useSyncState] ${integrationId}:`, {
      status: result.status,
      message: result.progress?.message,
      timestamp: result.timestamp
    });
  }

  return result;
}
